import React, { memo, useEffect } from 'react'

import { Button, Typography, makeStyles, Box, Divider, IconButton } from '@material-ui/core'

import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'

import clsx from 'clsx'
import { BillingPeriod } from '@stigg/js-client-sdk'
import { logToAnalytics } from 'modules'

import {
    BucketType,
    StartCheckoutType,
    UpdateBucketType,
    MappedPlanType,
    useNotification,
    MAX_PLAN_UNITS,
    useBoolean,
    UserToDowngradeType
} from 'hooks'

import { SCREEN } from './helpers'
import { DowngradeConfirmationDialog } from './DowngradeConfirmationDialog'

const useStyles = makeStyles(theme => ({
    title: {
        fontSize: '24px',
        fontWeight: theme.fontWeightBold,
        textAlign: 'center'
    },
    planItem: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '6px',
        padding: theme.spacing(4, 3, 3),
        margin: theme.spacing(0, 2),
        border: '3px solid #26c6da'
    },
    recommended: {
        position: 'absolute',
        left: 0,
        top: theme.spacing(1),
        color: '#fff',
        padding: '7px 20px 5px 7px',
        background: '#26c6da'
    },
    square: {
        position: 'absolute',
        width: '22px',
        height: '22px',
        right: '-20px',
        top: '50%',
        background: '#fff',
        transform: 'translate(50%, -50%) rotate(45deg)'
    },
    price: {
        fontSize: '20px',
        fontWeight: theme.fontWeightBold,
        textAlign: 'center',
        color: '#26c6da',
        marginTop: theme.spacing(2)
    },
    switcher: {
        borderRadius: '4px',
        display: 'flex',
        overflow: 'hidden',
        cursor: 'pointer',
        border: '1px solid #e0e0e0'
    },
    switcherItem: {
        padding: theme.spacing(1, 2)
    },
    active: {
        color: '#fff',
        background: '#26c6da'
    },
    counter: {
        width: '380px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #78909c',
        paddingBottom: theme.spacing(1)
    },
    counterDesc: {
        color: '#78909c'
    },
    continueButton: {
        marginTop: theme.spacing(6),
        backgroundColor: '#26c6da',
        '&:hover': {
            backgroundColor: '#26c6da'
        }
    },
    bold: {
        fontWeight: theme.fontWeightBold
    }
}))

type Props = {
    bucket: BucketType
    startCheckout: StartCheckoutType
    setStep: React.Dispatch<React.SetStateAction<number>>
    updateBucket: UpdateBucketType
    initialUserSeatsToBuy: number
    activePlanPeriod: BillingPeriod | null
    userSeatsQuota: number
    plan?: MappedPlanType
    isTrialPlan: boolean
    usedUsersSeats: number
    fetchUsersToDowngrade?: (input: void) => Promise<UserToDowngradeType[] | undefined>
    usersToDowngrade?: Array<UserToDowngradeType>
}

export const SelectSeats = memo(
    ({
        bucket,
        startCheckout,
        setStep,
        updateBucket,
        plan,
        initialUserSeatsToBuy,
        activePlanPeriod,
        userSeatsQuota,
        isTrialPlan,
        usedUsersSeats,
        fetchUsersToDowngrade,
        usersToDowngrade
    }: Props) => {
        const classes = useStyles()
        const { showWarningNotification } = useNotification()

        const { period, totalPrice, planId, totalLicenses, pricePerUnitMonthly } = bucket
        const downgradeDialog = useBoolean()

        if (!plan) return null

        const { planName, pricePoints, metadata } = plan
        const totalPricePerMonth =
            period === BillingPeriod.Monthly ? totalPrice : Math.ceil(totalPrice / 12)
        const incrementSeatsBy = metadata.incrementSeatsBy || 1
        const minUnitQuantity = metadata.minUnitQuantity?.[period] || 1
        const maxUnitQuantity = metadata.maxUnitQuantity?.[period] || MAX_PLAN_UNITS

        const setPeriod = (period: BillingPeriod) => updateBucket({ period })
        const setSeats = (v: number) => updateBucket({ totalLicenses: totalLicenses + v })

        const goToCheckoutScreen = () => {
            setStep(SCREEN.PaymentConfirmation)
            return startCheckout({
                planId,
                unitQuantity: totalLicenses,
                billingPeriod: period
            })
        }

        const handleContinue = async () => {
            if (
                bucket.totalLicenses < Math.max(userSeatsQuota, usedUsersSeats) ||
                !userSeatsQuota
            ) {
                const users = await fetchUsersToDowngrade?.()

                if (users?.length) return downgradeDialog.setTrue()
            }

            return goToCheckoutScreen()
        }

        useEffect(() => {
            if (totalLicenses !== 0 && totalLicenses >= minUnitQuantity) return

            updateBucket({
                totalLicenses: Math.max(initialUserSeatsToBuy, minUnitQuantity)
            })
        }, [
            initialUserSeatsToBuy,
            metadata.minUnitQuantity,
            minUnitQuantity,
            period,
            totalLicenses,
            updateBucket
        ])

        return (
            <Box className={classes.planItem}>
                {downgradeDialog.isTrue && !!usersToDowngrade?.length && (
                    <DowngradeConfirmationDialog
                        onConfirm={goToCheckoutScreen}
                        onClose={downgradeDialog.setFalse}
                        newSeatsAmount={totalLicenses}
                        usedUsersSeats={usedUsersSeats}
                        usersList={usersToDowngrade.map(({ email }) => email)}
                    />
                )}
                {metadata.isRecommended && (
                    <Box className={classes.recommended}>
                        <Box position="relative">
                            <Box className={classes.square} />
                            <Typography
                                variant="body2"
                                style={{
                                    fontWeight: 700
                                }}
                            >
                                Recommended
                            </Typography>
                        </Box>
                    </Box>
                )}
                <Typography className={classes.title}>{planName}</Typography>
                <Typography className={classes.price} style={{ position: 'relative' }}>
                    ${pricePerUnitMonthly} / Seat / Month
                </Typography>

                <Box className={classes.switcher} mt={3}>
                    {pricePoints.find(
                        ({ billingPeriod }) => billingPeriod === BillingPeriod.Monthly
                    ) && (
                        <Box
                            data-cy="upgrade-monthly"
                            onClick={() => {
                                if (activePlanPeriod === BillingPeriod.Annually && !isTrialPlan) {
                                    return showWarningNotification(
                                        'Please, contact sales to make this change'
                                    )
                                }

                                logToAnalytics('select_monthly_plan_type')
                                setPeriod(BillingPeriod.Monthly)
                            }}
                            className={clsx(classes.switcherItem, {
                                [classes.active]: period === BillingPeriod.Monthly
                            })}
                        >
                            Monthly
                        </Box>
                    )}
                    <Box
                        data-cy="upgrade-annualy"
                        onClick={() => {
                            logToAnalytics('select_annual_plan_type')
                            setPeriod(BillingPeriod.Annually)
                        }}
                        className={clsx(classes.switcherItem, {
                            [classes.active]: period === BillingPeriod.Annually
                        })}
                    >
                        Yearly
                    </Box>
                </Box>
                <Box width="100%" my={4}>
                    <Divider />
                </Box>
                <Box mt={3}>
                    <Typography>Select number of seats</Typography>
                </Box>
                <Box className={classes.counter}>
                    <IconButton
                        onClick={() => {
                            if (
                                activePlanPeriod === BillingPeriod.Annually &&
                                totalLicenses - incrementSeatsBy < userSeatsQuota &&
                                !isTrialPlan
                            ) {
                                return showWarningNotification(
                                    'Please, contact sales to make this change'
                                )
                            }

                            logToAnalytics('remove_seat_in_plan_configuration', {
                                seats: totalLicenses
                            })
                            setSeats(-incrementSeatsBy)
                        }}
                        disabled={totalLicenses <= minUnitQuantity}
                    >
                        <RemoveIcon data-cy="upgrade-remove-seats" />
                    </IconButton>
                    <Typography
                        variant="h4"
                        style={{
                            fontWeight: 600
                        }}
                    >
                        {totalLicenses}
                    </Typography>
                    <IconButton
                        onClick={() => {
                            logToAnalytics('add_seat_in_plan_configuration', {
                                seats: totalLicenses
                            })
                            setSeats(incrementSeatsBy)
                        }}
                        disabled={totalLicenses >= maxUnitQuantity}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
                <Typography
                    data-cy="upgradePricePerUnitMonthly"
                    variant="caption"
                    className={classes.counterDesc}
                >
                    {totalLicenses >= usedUsersSeats ? (
                        <>
                            You can always add more to your plan for ${pricePerUnitMonthly} / seat /
                            month
                        </>
                    ) : (
                        <>
                            You are about to purchase {totalLicenses} seats, while your account has{' '}
                            {usedUsersSeats} users
                        </>
                    )}
                </Typography>

                <Button
                    data-cy="upgrade-continue-to-payment-button"
                    className={classes.continueButton}
                    onClick={handleContinue}
                >
                    Continue to payment
                </Button>

                <Box mt={2}>
                    <Typography
                        data-cy="upgrade-totalPricePerMonth"
                        variant="caption"
                        className={classes.bold}
                    >
                        Subtotal: ${totalPricePerMonth}/month
                    </Typography>
                </Box>
            </Box>
        )
    }
)
